import { useEffect, useState, type ReactNode } from "react";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { useClientSectionNa } from "@/hooks/useClientSectionNa";
import { boardTable } from "@shared/interview/board";
import { cellOffFor, cellOptsFor, cellPhFor, optLabel, tableOf, type TileCell, type TileStep } from "@shared/interview/board-ch3";
import { OPTION_VALUE, isMoneyCell } from "@shared/interview/board-map";
import { FIELD_KEYS } from "@shared/constants/fields";
import { useSectionRows, type SectionRow } from "../useSectionRows";
import { saveInBackground } from "../saveFailure";
import { isSeededDefaultRow } from "@shared/constants/locked-rows";
import { TilePicker, type Tile, type TileSelection } from "../TilePicker";
import { TileTables, type TiledRow } from "../TileTables";
import { ContactCard, LinkedPersonCell, cardSummary, slotName, type CardSlot } from "../PersonPicker";
import { IconTextCell, OffCell, PickerCell } from "../InterviewCells";
import { LocationBox, LocationBoxPanel } from "../LocationBox";
import { OtherBox, OTHER_WORD, withTypedOther } from "../OtherBox";
import { OnePanelProvider, usePanels } from "../useOnePanelOpen";

/**
 * s237 (chapter three PR 2; D-236.61) — THE SHARED TILE-TO-ROW ENGINE, piloted on chapter three's Work screen.
 *
 * One screen from a chapter's generated board step (chapter ONE's model: tiles → one row per pick → the step's table, or
 * its `second` table for the tiles it names), written through a per-screen binding map. Built on the existing pieces —
 * TilePicker, TileTables / InterviewRowTable, the cells, the contact and company cards, the location box, the Other box —
 * so a row here behaves exactly as a chapter-one row does (rule 159). Chapters 4–6 grow it; chapter two keeps its own
 * engine until CH2-ONTO-SHARED-ENGINE-1 (D-236.62).
 *
 * What the BOARD declares is read from the data (rule 137): tiles and the none tile, `pairNone`, headers and widths, each
 * cell's kind, example, list and Other words, per-tile rules (`false` = "not asked", `ph`, `opts`), the tables' lead and
 * note lines. What the board does NOT carry — where each answer is stored — is the binding (EngineBinding).
 *
 * Storage: a screen may save to more than one section (Work: job / other-work rows are Career rows, business rows are
 * Business rows — D-237.27); each source is one typed section through `useSectionRows`, and a row's kind column says which
 * tile it is. An empty kind reads as the source's default tile (D-237.27).
 *
 * 🔴 Deselect, None and the bin HIDE rows; nothing is deleted (G8). The None answer is saved in the existing section N/A
 * store (`client_section_na`, D-237.26) for each section the binding names, and read back.
 */

/** One section the screen's rows are saved to. */
export type EngineSource = {
  categorySlug: string;
  sectionSlug: string;
  /** The row's kind column (a select field) and each of this source's tiles' stored kind. */
  kindField: string;
  kindOf: Readonly<Record<string, string>>;
  /** The tile a row with no kind reads as (D-237.27). */
  defaultTile: string;
  /** A typed tile's lead (the business's name), by tile id. */
  leadField?: Readonly<Record<string, string>>;
};

/** Where a board cell's answer lives. */
export type CellBinding =
  | { kind: "card"; slots: readonly CardSlot[]; moreLabel?: string; openAddressWhenEmpty?: boolean }
  | { kind: "field"; key: string };

export type EngineBinding = {
  /** The board screen's key (OPTION_VALUE, test ids). */
  screen: string;
  sources: readonly EngineSource[];
  /** Board cell key → where it is stored. Every cell the board draws on a built screen is bound. */
  cells: Readonly<Record<string, CellBinding>>;
  /** The sections the None answer is saved for (each must be manual-N/A eligible, server-enforced). */
  noneSections: readonly string[];
  /** A row note (rule 156) shown while a cell holds an answer — Work's "Who takes over" (D-237.28). */
  rowNote?: { cell: string; noteKey: string };
};

type Row = SectionRow & { source: number; tileId: string };

/** The tile a row is, from its kind column — or null when the kind belongs to another screen (a past employer). Pure. */
export function tileOfRow(src: EngineSource, values: Readonly<Record<string, string>>): string | null {
  const kind = (values[src.kindField] ?? "").trim();
  if (!kind) return src.defaultTile;
  return Object.entries(src.kindOf).find(([, v]) => v === kind)?.[0] ?? null;
}

/** Every stored key a cell writes. Pure. */
export function keysOf(b: CellBinding | undefined): string[] {
  if (!b) return [];
  if (b.kind === "field") return [b.key];
  return b.slots.flatMap((s) => [
    ...(s.name.kind === "nameParts" ? [s.name.first, s.name.last] : [s.name.key]),
    ...(s.phone ? [s.phone] : []), ...(s.email ? [s.email] : []), ...(s.address ? [s.address] : []),
  ]);
}

/** A row is an answer once it holds anything its table asks, its typed lead, or its Note. Pure. */
export function rowHasContent(step: TileStep, binding: EngineBinding, row: Pick<Row, "values" | "tileId" | "source">): boolean {
  const table = tableOf(step, row.tileId);
  const src = binding.sources[row.source];
  const keys = [...table.cells.flatMap((c) => keysOf(binding.cells[c.k])), FIELD_KEYS.NOTES, ...(src.leadField?.[row.tileId] ? [src.leadField[row.tileId]] : [])];
  return keys.some((k) => (row.values[k] ?? "").trim() !== "");
}

/** The picker's options for a tile: the board's words, stored as OPTION_VALUE says (else as the words). Pure. */
export function pickerOptions(screen: string, table: Parameters<typeof cellOptsFor>[0], tileId: string, cell: TileCell): { value: string; label: string }[] {
  const map = OPTION_VALUE[screen]?.[cell.k];
  return cellOptsFor(table, tileId, cell).map((o) => { const label = optLabel(o); return { value: map?.[label] ?? label, label }; });
}

export function TileRowEngine(props: { step: TileStep; binding: EngineBinding; clientId: string; onAnswered: () => void }) {
  return (
    <OnePanelProvider>
      <EngineBody {...props} />
    </OnePanelProvider>
  );
}

function EngineBody({ step, binding, clientId, onAnswered }: { step: TileStep; binding: EngineBinding; clientId: string; onAnswered: () => void }) {
  // The sources are fixed per screen (a constant binding), so the hook count never changes between renders.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const data = binding.sources.map((s) => useSectionRows(clientId, s.categorySlug, s.sectionSlug, onAnswered, { typed: true }));
  const na = useClientSectionNa(clientId);
  const panels = usePanels();
  const createFlight = useSingleFlight();
  const [createdHere, setCreatedHere] = useState<ReadonlySet<string>>(() => new Set());
  const [removedHere, setRemovedHere] = useState<ReadonlyMap<string, string>>(() => new Map());

  const group = step.groups?.[0];
  const tiles: Tile[] = (group?.tiles ?? []).map(({ id, label, sub, mark, color }) => ({ id, label, sub, mark, color }));
  const tileById = new Map((group?.tiles ?? []).map((t) => [t.id, t]));
  const sourceOfTile = (tileId: string) => binding.sources.findIndex((s) => tileId in s.kindOf);

  // A seeded `__seeded__…` scaffold is never a record (chapter two's rule, ch2Bindings `isRecordRow`): it is not drawn and a
  // tile never adopts it as its new row — /typed-rows refuses its id, so every answer to it was lost (s237, client@).
  const rows: Row[] = data.flatMap((d, i) => d.rows.flatMap((r) => {
    if (isSeededDefaultRow(r.rowId)) return [];
    const tileId = tileOfRow(binding.sources[i], r.values);
    return tileId && tileById.has(tileId) ? [{ ...r, source: i, tileId }] : [];
  }));
  const visible = rows.filter((r) => !r.hidden);
  // The None answer is read back from the section N/A list, so the seed waits for that list too (D-237.26).
  const ready = data.every((d) => d.ready) && (binding.noneSections.length === 0 || na.loaded);

  const [selection, setSelection] = useState<TileSelection | null>(null);
  // Seeded ONCE from what the plan holds, then owned here — a refetch mid-answer must not flip a pressed tile.
  useEffect(() => {
    if (selection || !ready) return;
    const selected = tiles.map((t) => t.id).filter((id) => visible.some((r) => r.tileId === id && rowHasContent(step, binding, r)));
    const none = selected.length === 0 && binding.noneSections.length > 0 && binding.noneSections.every((s) => na.naSlugs.has(s));
    setSelection({ selected, none });
  }, [selection, ready, visible, na.naSlugs]);
  const sel = selection ?? { selected: [], none: false };

  const create = (tileId: string) => {
    const i = sourceOfTile(tileId);
    const src = binding.sources[i];
    return createFlight.run(() => data[i].createRow({ [src.kindField]: src.kindOf[tileId] }))
      .then((id) => { if (id) setCreatedHere((s) => new Set(s).add(id)); });
  };
  const refreshAll = () => data.forEach((d) => d.refresh());

  const turnOn = async (tileId: string, wasNone: boolean) => {
    // A tile after None of these takes the saved None back — whether or not the N/A list has refetched yet.
    for (const s of binding.noneSections) if (wasNone || na.naSlugs.has(s)) na.bringBack(s);
    const ofTile = rows.filter((r) => r.tileId === tileId);
    if (ofTile.some((r) => r.hidden)) {
      for (const r of ofTile.filter((x) => x.hidden)) await data[r.source].setRowHidden(r.rowId, false);
      refreshAll();
    } else if (ofTile.length === 0) {
      await create(tileId);
    } else if (!ofTile.some((r) => rowHasContent(step, binding, r))) {
      setCreatedHere((s) => new Set(s).add(ofTile[ofTile.length - 1].rowId));
    }
  };
  const turnOff = async (tileId: string) => {
    for (const r of rows.filter((x) => x.tileId === tileId && !x.hidden)) await data[r.source].setRowHidden(r.rowId, true);
    refreshAll();
  };
  const onTiles = async (next: TileSelection, changed: string) => {
    setSelection(next);
    if (changed === "none") {
      if (!next.none) { for (const s of binding.noneSections) na.bringBack(s); return; }
      // None of these: the rows it replaces are hidden (never deleted), and the answer is saved (D-237.26).
      for (const r of visible) await data[r.source].setRowHidden(r.rowId, true);
      refreshAll();
      for (const s of binding.noneSections) na.markNa(s);
      return;
    }
    if (next.selected.includes(changed)) await turnOn(changed, sel.none);
    else await turnOff(changed);
  };

  // The bin HIDES the row and leaves "… removed · Undo" in its place for this visit (G8).
  const removeRow = async (rowId: string) => {
    const r = rows.find((x) => x.rowId === rowId);
    if (!r) return;
    const lead = binding.sources[r.source].leadField?.[r.tileId];
    const label = (lead ? (r.values[lead] ?? "").trim() : "") || tileById.get(r.tileId)?.label || "This row";
    setRemovedHere((m) => new Map(m).set(rowId, label));
    try {
      await data[r.source].setRowHidden(rowId, true);
    } catch (err) {
      setRemovedHere((m) => { const n = new Map(m); n.delete(rowId); return n; });
      throw err;
    }
    refreshAll();
  };
  const undoRemove = async (r: Row) => {
    await data[r.source].setRowHidden(r.rowId, false);
    setRemovedHere((m) => { const n = new Map(m); n.delete(r.rowId); return n; });
    refreshAll();
  };

  const drawn = rows.filter((r) => {
    if (removedHere.has(r.rowId)) return true;
    if (r.hidden) return false;
    return sel.selected.includes(r.tileId) && (rowHasContent(step, binding, r) || createdHere.has(r.rowId));
  });

  const noted: { second: boolean }[] = [];
  const tiled: TiledRow[] = drawn.map((r) => {
    const tile = tileById.get(r.tileId);
    const lead = tile?.label ?? "";
    if (removedHere.has(r.rowId)) {
      return { rowId: r.rowId, tileId: r.tileId, lead, cells: [], removed: { label: removedHere.get(r.rowId)!, onUndo: () => saveInBackground(undoRemove(r)) } };
    }
    const d = data[r.source];
    const src = binding.sources[r.source];
    const table = tableOf(step, r.tileId);
    const columns = boardTable(table as never).columns; // boardTable reads only the cells' keys
    const v = r.values;
    const save = (changes: Record<string, string>) => d.writeCells(r.rowId, changes);
    const tid = (what: string) => `interview-${binding.screen}-${what}-${r.rowId}`;
    const panelNodes: ReactNode[] = [];
    const under: ReactNode[] = [];

    const cells = table.cells.map((c) => {
      const header = columns.find((x) => x.key === c.k)?.label ?? "";
      if (cellOffFor(table, r.tileId, c.k)) return <OffCell key={c.k} testId={tid(c.k)} />;
      const b = binding.cells[c.k];
      if (!b) throw new Error(`engine: board cell "${binding.screen}.${c.k}" has no binding`);
      if (b.kind === "card") {
        const key = `${r.rowId}:${c.k}`;
        const isOrg = !!c.org;
        if (panels.isOpen(key)) {
          panelNodes.push(
            <ContactCard interviewLook key={key} title={c.title ?? ""} slots={b.slots} values={v} fieldIdByKey={d.fieldIdByKey} clientId={clientId}
              undo={c.undo} moreLabel={b.moreLabel} addressOpenWhenEmpty={b.openAddressWhenEmpty}
              onSave={save} onClose={() => panels.close(key)} testId={tid(`${c.k}-card`)} />,
          );
        }
        return <LinkedPersonCell key={c.k} value="" label={cardSummary(b.slots, v)} word={c.word ?? ""} org={isOrg}
          open={panels.isOpen(key)} onOpen={() => panels.toggle(key)} testId={tid(c.k)} />;
      }
      const f = b.key;
      const held = v[f] ?? "";
      if (c.kind === "picker") {
        const listed = pickerOptions(binding.screen, table, r.tileId, c);
        const otherValue = listed.find((o) => o.label === OTHER_WORD)?.value;
        const isOther = (val: string) => !!otherValue && val === otherValue;
        const typed = isOther(held) ? (r.specify?.[f] ?? "") : "";
        const offered = withTypedOther(listed, isOther, typed);
        const options = held && !offered.some((o) => o.value === held) ? [...offered, { value: held, label: held }] : offered; // G8
        if (isOther(held)) {
          under.push(<OtherBox key={`${c.k}-other`} label={c.otherLabel} placeholder={c.otherPh} value={r.specify?.[f] ?? ""}
            onCommit={(t) => saveInBackground(d.writeSpecify(r.rowId, f, t))} testId={tid(`${c.k}-other`)} />);
        }
        return <PickerCell key={c.k} value={held} options={options} placeholder={c.ph} ariaLabel={header}
          onCommit={(val) => saveInBackground(save({ [f]: val }))} testId={tid(c.k)} />;
      }
      if (c.long) {
        const key = `${r.rowId}:${c.k}`;
        if (panels.isOpen(key)) {
          panelNodes.push(<LocationBoxPanel key={key} title={c.bigTitle ?? header} hint={c.bigHint} value={held}
            onCommit={(val) => save({ [f]: val })} onClose={() => panels.close(key)} testId={tid(c.k)} />);
        }
        return <LocationBox key={c.k} value={held} placeholder={cellPhFor(table, r.tileId, c)} open={panels.isOpen(key)}
          onOpen={() => panels.toggle(key)} onCommit={(val) => save({ [f]: val })} testId={tid(c.k)} />;
      }
      return <IconTextCell key={c.k} value={held} placeholder={cellPhFor(table, r.tileId, c)} ariaLabel={header} money={isMoneyCell(c.k)}
        onCommit={(val) => saveInBackground(save({ [f]: val }))} testId={tid(c.k)} />;
    });

    // Rule 156: the row's note while its cell holds an answer (D-237.28: "Who takes over" only).
    const rn = binding.rowNote;
    if (rn && table.cells.some((c) => c.k === rn.cell)) {
      const nb = binding.cells[rn.cell];
      const named = nb?.kind === "card" ? nb.slots.some((s) => slotName(s, v)) : nb ? (v[nb.key] ?? "").trim() !== "" : false;
      if (named && step.noteFor?.[rn.noteKey]) noted.push({ second: table.second });
    }

    const leadField = src.leadField?.[r.tileId];
    const noteKey = `${r.rowId}:note`;
    return {
      rowId: r.rowId,
      tileId: r.tileId,
      lead,
      ...(tile?.mark && tile.color ? { leadMark: { mark: tile.mark, color: tile.color } } : {}),
      ...(leadField ? { leadInput: { value: v[leadField] ?? "", placeholder: tile?.full, onCommit: (val: string) => saveInBackground(save({ [leadField]: val })), testId: tid("lead") } } : {}),
      cells,
      canAddRow: !(step.noDup ?? []).includes(r.tileId),
      note: {
        value: v[FIELD_KEYS.NOTES] ?? "",
        open: panels.isOpen(noteKey),
        onToggle: () => panels.toggle(noteKey),
        onCommit: (val: string) => (val !== (v[FIELD_KEYS.NOTES] ?? "") ? save({ [FIELD_KEYS.NOTES]: val }) : undefined),
      },
      lineOnePanels: under.length ? <div className="mb-3 flex flex-col gap-4 pl-0.5" data-testid={tid("under")}>{under}</div> : null,
      panels: panelNodes.length ? <>{panelNodes}</> : null,
    };
  });

  const rowNoteText = binding.rowNote ? step.noteFor?.[binding.rowNote.noteKey] : undefined;
  const firstTable = boardTable({ cols: step.cols, headers: step.headers, cells: step.cells as never, rowsLead: step.rowsLead, rowsNote: step.rowsNote });
  const second = step.second ? { ...boardTable(step.second as never), tiles: step.second.tiles } : undefined;
  const none = group?.none ? { label: group.none.label, sub: group.none.sub || undefined, prune: group.none.prune, delta: group.none.delta } : undefined;

  return (
    <div data-testid={`interview-${binding.screen}`}>
      <TilePicker compact={!!(step as { compact?: boolean }).compact} tiles={tiles} selection={sel} onChange={(n, c) => saveInBackground(onTiles(n, c))} none={none}
        columns={(step.pairNone ? 2 : (group?.cols ?? 3)) as 2 | 3 | 4 | 5} testIdPrefix={`interview-${binding.screen}`} />
      <TileTables rows={tiled} first={firstTable} second={second}
        onAddRow={(rowId) => { const r = rows.find((x) => x.rowId === rowId); if (r) saveInBackground(create(r.tileId)); }}
        onRemove={(id) => saveInBackground(removeRow(id))} testIdPrefix={`interview-${binding.screen}-tables`} cutLead rowMarks strongRowLine
        firstNotes={rowNoteText && noted.some((n) => !n.second) ? [{ text: rowNoteText }] : []}
        secondNotes={rowNoteText && noted.some((n) => n.second) ? [{ text: rowNoteText }] : []} />
    </div>
  );
}
